import React, { useState, useCallback } from 'react';
import { Search, Package, Filter, User, CheckCircle, MapPin, ChevronRight, ChevronDown, Lock } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { putAwayService } from '../../../../services/putAwayService';
import { StcIntRelOrderDto } from '../../../../types/putaway';
import { PreviewPutAwayModal } from './PreviewPutAwayModal';
import { PutAwayCard } from './PutAwayCard';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useAuth } from '../../../../contexts/AuthContext';

interface ModernPutAwayListProps {
  putAwayOrders: StcIntRelOrderDto[];
  onSelectPutAway: (putAwayOrder: StcIntRelOrderDto) => Promise<void>;
  onNextPutAway: () => Promise<void>;
  onRefreshOrders: () => void;
  onBack?: () => void;
}

export const ModernPutAwayList: React.FC<ModernPutAwayListProps> = ({
  putAwayOrders,
  onSelectPutAway,
  onNextPutAway,
  onRefreshOrders,
  onBack
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingNextPutAway, setLoadingNextPutAway] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewStcOrderNo, setPreviewStcOrderNo] = useState<number | null>(null);
  const [isMyPutAwayExpanded, setIsMyPutAwayExpanded] = useState(true);
  const [isAvailablePutAwayExpanded, setIsAvailablePutAwayExpanded] = useState(true);
  const [isLockedByOthersExpanded, setIsLockedByOthersExpanded] = useState(true);

  // Lagersjef (2) og administrator (3) ser utlegg låst av andre operatører og kan tvangs-opplåse
  const isManagerOrAdmin = user?.operatorGroupId === '2' || user?.operatorGroupId === '3';
  const myOperatorId = (user?.operatorId || '').toLowerCase();

  // Optimized callback functions with useCallback to prevent unnecessary re-renders
  const handleLockPutAwayOrder = useCallback(async (stcOrderNo: number) => {
    try {
      await putAwayService.lockPutAwayOrder(stcOrderNo);
      // Oppdater listen for å vise den nye låsstatusen
      onRefreshOrders();
    } catch (err) {
      console.error('Error locking put-away order:', err);
      alert(t('warehouse.modules.putaway.errors.lockOrderFailed'));
    }
  }, [onRefreshOrders]);

  const handleUnlockPutAwayOrder = useCallback(async (stcOrderNo: number) => {
    try {
      await putAwayService.unlockPutAwayOrder(stcOrderNo);
      // Oppdater listen for å vise den nye låsstatusen
      onRefreshOrders();
    } catch (err) {
      console.error('Error unlocking put-away order:', err);
      alert(t('warehouse.modules.putaway.errors.unlockOrderFailed'));
    }
  }, [onRefreshOrders]);

  const handleStartPutAway = useCallback(async (putAwayOrder: StcIntRelOrderDto) => {
    try {
      await onSelectPutAway(putAwayOrder);
    } catch (err) {
      console.error('Error starting put-away:', err);
      // Error is already handled in onSelectPutAway
    }
  }, [onSelectPutAway]);

  const handleNextPutAway = async () => {
    try {
      setLoadingNextPutAway(true);
      await onNextPutAway();
    } catch (err) {
      console.error('Error starting next put-away:', err);
      // Error is already handled in onNextPutAway
    } finally {
      setLoadingNextPutAway(false);
    }
  };

  const handlePreviewPutAway = useCallback((stcOrderNo: number) => {
    setPreviewStcOrderNo(stcOrderNo);
    setShowPreviewModal(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewStcOrderNo(null);
  }, []);

  // Del opp utplasseringsordrer basert på LockedBy-status.
  // «Mine» må sammenlignes mot egen operatør-ID: for lagersjef/administrator returnerer
  // backend også utlegg som er låst til andre operatører.
  const isLockedByMe = (order: StcIntRelOrderDto) =>
    !!order.lockedBy && order.lockedBy.trim().toLowerCase() === myOperatorId;

  const myPutAwayOrders = putAwayOrders.filter(isLockedByMe);

  const availablePutAwayOrders = putAwayOrders.filter(order =>
    !order.lockedBy || order.lockedBy.trim() === ''
  );

  const lockedByOthersOrders = putAwayOrders.filter(order =>
    order.lockedBy && order.lockedBy.trim() !== '' && !isLockedByMe(order)
  );

  // Filtrer basert på søkebegrep
  const matchesSearch = (order: StcIntRelOrderDto) =>
    order.description1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.companyNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.stcOrderNo.toString().includes(searchTerm) ||
    order.lockedBy?.toLowerCase().includes(searchTerm.toLowerCase());

  const filteredMyOrders = myPutAwayOrders.filter(matchesSearch);
  const filteredAvailableOrders = availablePutAwayOrders.filter(matchesSearch);
  const filteredLockedByOthersOrders = lockedByOthersOrders.filter(matchesSearch);

  // Slett utplasseringsordre (kun lagersjef/administrator) — grunnlaget legges tilbake i
  // Grunnlag-skjermen (LineStatus 30 -> 20) slik at det kan genereres på nytt derfra
  const handleDeleteOrder = useCallback(async (order: StcIntRelOrderDto) => {
    const confirmed = window.confirm(
      t('warehouse.modules.putaway.messages.confirmDelete',
        { '0': String(order.stcOrderNo), '1': String(order.lineCount || 0) },
        `Utplasseringsordre #${order.stcOrderNo} (${order.lineCount || 0} linjer) slettes, og grunnlaget legges tilbake i Grunnlag-skjermen. Vil du fortsette?`)
    );
    if (!confirmed) return;

    try {
      const result = await putAwayService.deletePutAwayOrder(order.stcOrderNo);
      alert(
        t('warehouse.modules.putaway.messages.deleteSuccess',
          { '0': String(order.stcOrderNo), '1': String(result.releasedGrunnlagCount) },
          `Ordre #${order.stcOrderNo} er slettet. ${result.releasedGrunnlagCount} grunnlagsrad(er) er lagt tilbake i Grunnlag-skjermen.`)
      );
      onRefreshOrders();
    } catch (err) {
      console.error('Error deleting put-away order:', err);
      alert(err instanceof Error ? err.message : t('warehouse.modules.putaway.errors.deleteOrderFailed', {}, 'Kunne ikke slette utplasseringsordren'));
    }
  }, [onRefreshOrders, t]);

  // Regenerer utplasseringsordre fra grunnlag (kun lagersjef/administrator) — brukes etter
  // endring i grunndata (f.eks. lokasjonskapasitet) slik at ordren får ny lokasjonsfordeling
  const handleRegenerateOrder = useCallback(async (order: StcIntRelOrderDto) => {
    const confirmed = window.confirm(
      t('warehouse.modules.putaway.messages.confirmRegenerate',
        { '0': String(order.stcOrderNo) },
        `Utplasseringsordre #${order.stcOrderNo} slettes og genereres på nytt av regelmotoren. Ny ordre kan få nytt ordrenummer og annen lokasjonsfordeling. Vil du fortsette?`)
    );
    if (!confirmed) return;

    try {
      const result = await putAwayService.regeneratePutAwayOrder(order.stcOrderNo);
      if (result.success) {
        alert(
          t('warehouse.modules.putaway.messages.regenerateSuccess',
            { '0': String(result.deletedOrderNo), '1': String(result.newOrderNo), '2': String(result.newLineCount) },
            `Ordre #${result.deletedOrderNo} er slettet — ny ordre #${result.newOrderNo} er opprettet (${result.newLineCount} linjer).`)
        );
      } else {
        // Ordren ble slettet, men regenereringen feilet — grunnlaget ligger klart i Grunnlag-skjermen
        alert(
          t('warehouse.modules.putaway.messages.regeneratePartialFailure',
            { '0': String(result.deletedOrderNo), '1': result.errorMessage || '' },
            `Ordre #${result.deletedOrderNo} er slettet, men regenereringen feilet: ${result.errorMessage || ''} Grunnlaget ligger klart i Grunnlag-skjermen og kan genereres derfra.`)
        );
      }
      onRefreshOrders();
    } catch (err) {
      console.error('Error regenerating put-away order:', err);
      alert(err instanceof Error ? err.message : t('warehouse.modules.putaway.errors.regenerateOrderFailed', {}, 'Kunne ikke regenerere utplasseringsordren'));
    }
  }, [onRefreshOrders, t]);

  // Tvangs-opplåsing av andres utlegg (kun lagersjef/administrator) — krever bekreftelse
  const handleForceUnlock = useCallback(async (stcOrderNo: number) => {
    const order = putAwayOrders.find(o => o.stcOrderNo === stcOrderNo);
    const lockedBy = order?.lockedBy || '';
    const confirmed = window.confirm(
      t('warehouse.modules.putaway.messages.confirmForceUnlock', { '0': String(stcOrderNo), '1': lockedBy },
        `Utleggsordre #${stcOrderNo} er låst til ${lockedBy}. Vil du låse den opp?`)
    );
    if (!confirmed) return;
    await handleUnlockPutAwayOrder(stcOrderNo);
  }, [putAwayOrders, handleUnlockPutAwayOrder, t]);

  // Render optimized put-away sections with collapsible support
  const renderPutAwaySection = (
    title: string,
    orders: StcIntRelOrderDto[],
    isOwnedSection: boolean,
    icon: React.ReactNode,
    isCollapsible: boolean = false,
    isExpanded: boolean = true,
    onToggleExpand?: () => void,
    isLockedByOtherSection: boolean = false
  ) => {
    if (orders.length === 0) return null;

    return (
      <div className="mb-4 sm:mb-8">
        <div
          className={`text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 ${
            isCollapsible ? 'cursor-pointer hover:text-green-600 transition-colors' : ''
          }`}
          onClick={isCollapsible ? onToggleExpand : undefined}
        >
          {isCollapsible && (
            isExpanded ? (
              <ChevronDown className="text-gray-600 flex-shrink-0" size={24} />
            ) : (
              <ChevronRight className="text-gray-600 flex-shrink-0" size={24} />
            )
          )}
          {icon}
          <span className="truncate">{title} ({orders.length})</span>
        </div>
        {isExpanded && (
          <div className="space-y-2 sm:space-y-3">
            {orders.map((order) => (
              <PutAwayCard
                key={order.stcOrderNo}
                order={order}
                isOwned={isOwnedSection}
                isLockedByOther={isLockedByOtherSection}
                isManagerOrAdmin={isManagerOrAdmin}
                onStartPutAway={handleStartPutAway}
                onLockOrder={handleLockPutAwayOrder}
                onUnlockOrder={isLockedByOtherSection ? handleForceUnlock : handleUnlockPutAwayOrder}
                onPreviewOrder={handlePreviewPutAway}
                onDeleteOrder={handleDeleteOrder}
                onRegenerateOrder={handleRegenerateOrder}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-3 sm:mb-6">
          {/* Header with title and search field - More compact on mobile */}
          <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4 mb-3 sm:mb-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <BackButton onClick={onBack || (() => {})} />
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                <span className="hidden sm:inline">{t('warehouse.modules.putaway.title')}</span>
                <span className="sm:hidden">{t('warehouse.modules.putaway.shortName')}</span>
              </h1>
            </div>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder={t('warehouse.modules.putaway.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {/* Action buttons - Full width on mobile, compact on desktop */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
            <button
              onClick={handleNextPutAway}
              disabled={loadingNextPutAway}
              className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 min-h-[44px] text-sm sm:text-base"
            >
              {loadingNextPutAway ? (
                <>
                  <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full" />
                  <span>{t('warehouse.modules.putaway.actions.starting')}</span>
                </>
              ) : (
                <>
                  <MapPin size={16} />
                  <span>{t('warehouse.modules.putaway.actions.nextPutaway')}</span>
                </>
              )}
            </button>

            <button
              onClick={onRefreshOrders}
              className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] text-sm sm:text-base"
            >
              <Filter size={16} />
              <span>{t('common.buttons.refresh')}</span>
            </button>
          </div>
        </div>

        {/* Mine utplasseringer-seksjonen */}
        {renderPutAwaySection(
          t('warehouse.modules.putaway.sections.myPutaways'),
          filteredMyOrders,
          true,
          <User className="text-green-600 flex-shrink-0" size={20} />,
          true,
          isMyPutAwayExpanded,
          () => setIsMyPutAwayExpanded(!isMyPutAwayExpanded)
        )}

        {/* Tilgjengelige utplasseringer-seksjonen */}
        {renderPutAwaySection(
          t('warehouse.modules.putaway.sections.availablePutaways'),
          filteredAvailableOrders,
          false,
          <MapPin className="text-green-600 flex-shrink-0" size={20} />,
          true,
          isAvailablePutAwayExpanded,
          () => setIsAvailablePutAwayExpanded(!isAvailablePutAwayExpanded)
        )}

        {/* Låst av andre operatører — kun for lagersjef/administrator (profilgruppe 2/3) */}
        {isManagerOrAdmin && renderPutAwaySection(
          t('warehouse.modules.putaway.sections.lockedByOthers', {}, 'Låst av andre operatører'),
          filteredLockedByOthersOrders,
          false,
          <Lock className="text-amber-600 flex-shrink-0" size={20} />,
          true,
          isLockedByOthersExpanded,
          () => setIsLockedByOthersExpanded(!isLockedByOthersExpanded),
          true
        )}

        {filteredAvailableOrders.length === 0 && filteredMyOrders.length === 0 &&
          (!isManagerOrAdmin || filteredLockedByOthersOrders.length === 0) && (
          <div className="text-center py-8 sm:py-12 text-gray-500">
            <MapPin size={40} className="sm:w-12 sm:h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-sm sm:text-base">{t('warehouse.modules.putaway.messages.noOrdersFound')}</p>
            {searchTerm && (
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                {t('warehouse.modules.putaway.messages.changeSearchOrRefresh')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      <PreviewPutAwayModal
        isOpen={showPreviewModal}
        stcOrderNo={previewStcOrderNo}
        onClose={handleClosePreview}
      />
    </div>
  );
};
